import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, MessageCircle, Flag, Trash2, CheckCircle2, MapPin, HelpCircle, Users, BedDouble, Car, Tag } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { UserAvatar, VipBadge, Pill } from "@/components/common";
import { useApi, errMsg, useFileUrl } from "@/lib/api";
import { useMe } from "@/context/MeContext";
import { timeAgo } from "@/lib/labels";

export const LISTING = {
  crew: { label: "Looking for crew", icon: Users },
  housing: { label: "Housing", icon: BedDouble },
  ride: { label: "Shared ride", icon: Car },
  other: { label: "Other", icon: Tag },
};

const Author = ({ a, at }) => (
  <Link to={`/u/${a.user_id}`} className="flex items-center gap-3 min-w-0">
    <UserAvatar user={a} size={40} />
    <div className="min-w-0">
      <p className="font-semibold text-slate-900 flex items-center gap-2 truncate">{a.name}<VipBadge user={a} /></p>
      <p className="text-xs text-slate-500 truncate">{a.role === "company" ? "Company" : a.headline || "Technician"} · {timeAgo(at)}</p>
    </div>
  </Link>
);

function report(api, target_type, target_id) {
  const reason = window.prompt("Why are you reporting this? (optional)");
  if (reason === null) return;
  api("post", "/community/reports", { target_type, target_id, reason }).then(() => toast.success("Reported to admins")).catch((e) => toast.error(errMsg(e)));
}

function Comments({ post, onAccepted, onCount }) {
  const api = useApi();
  const { me } = useMe();
  const [list, setList] = useState(null);
  const [text, setText] = useState("");
  useEffect(() => { api("get", `/community/posts/${post.id}/comments`).then(setList).catch((e) => toast.error(errMsg(e))); }, [api, post.id]);
  if (list === null) return <p className="text-sm text-slate-400 py-3">Loading...</p>;
  const add = async (e) => {
    e.preventDefault();
    try { const c = await api("post", `/community/posts/${post.id}/comments`, { text }); setList([...list, c]); setText(""); onCount(1); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const del = async (id) => {
    try { await api("delete", `/community/comments/${id}`); setList(list.filter((c) => c.id !== id)); onCount(-1); } catch (err) { toast.error(errMsg(err)); }
  };
  const accept = async (id) => {
    try { onAccepted((await api("post", `/community/posts/${post.id}/accept/${id}`)).accepted_comment_id); } catch (err) { toast.error(errMsg(err)); }
  };
  const canAccept = post.kind === "question" && post.author_id === me.user_id;
  const sorted = [...list].sort((a, b) => (b.id === post.accepted_comment_id) - (a.id === post.accepted_comment_id));
  return (
    <div className="mt-4 space-y-3 border-t border-slate-100 pt-4">
      {sorted.map((c) => {
        const accepted = c.id === post.accepted_comment_id;
        return (
          <div key={c.id} data-testid={`comment-${c.id}`} className={`rounded p-3 ${accepted ? "bg-emerald-50 border border-emerald-200" : "bg-slate-50"}`}>
            <div className="flex justify-between gap-2">
              <Author a={c.author} at={c.created_at} />
              <div className="flex items-start gap-1 shrink-0">
                {canAccept && <Button size="sm" variant="ghost" onClick={() => accept(c.id)} data-testid={`comment-accept-${c.id}`} className={accepted ? "text-emerald-700" : ""}><CheckCircle2 size={15} />{accepted ? "Best answer" : "Accept"}</Button>}
                {!canAccept && accepted && <Pill cls="bg-emerald-100 text-emerald-800 border-emerald-200"><CheckCircle2 size={11} />Best answer</Pill>}
                {(c.author_id === me.user_id || me.access_role === "admin")
                  ? <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => del(c.id)} data-testid={`comment-delete-${c.id}`} aria-label="Delete"><Trash2 size={14} /></Button>
                  : <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => report(api, "comment", c.id)} data-testid={`comment-report-${c.id}`} aria-label="Report"><Flag size={14} /></Button>}
              </div>
            </div>
            <p className="mt-2 text-sm text-slate-800 whitespace-pre-line">{c.text}</p>
          </div>
        );
      })}
      <form onSubmit={add} className="flex gap-2 items-end">
        <Textarea rows={1} value={text} onChange={(e) => setText(e.target.value)} placeholder={post.kind === "question" ? "Write an answer..." : "Write a comment..."} className="min-h-[40px]" data-testid={`comment-input-${post.id}`} />
        <Button type="submit" disabled={!text.trim()} data-testid={`comment-submit-${post.id}`}>Send</Button>
      </form>
    </div>
  );
}

export default function PostCard({ post: initial, onDeleted }) {
  const api = useApi();
  const { me } = useMe();
  const [post, setPost] = useState(initial);
  const [open, setOpen] = useState(false);
  const img = useFileUrl(post.image_path);
  const L = LISTING[post.listing_type];

  const like = async () => {
    try { setPost({ ...post, ...(await api("post", `/community/posts/${post.id}/like`)) }); } catch (e) { toast.error(errMsg(e)); }
  };
  const del = async () => {
    if (!window.confirm("Delete this post?")) return;
    try { await api("delete", `/community/posts/${post.id}`); toast.success("Post deleted"); onDeleted?.(post.id); } catch (e) { toast.error(errMsg(e)); }
  };
  const mine = post.author_id === me.user_id || me.access_role === "admin";

  return (
    <article data-testid={`post-card-${post.id}`} className="rise panel p-5 sm:p-6">
      <div className="flex justify-between gap-3">
        <Author a={post.author} at={post.created_at} />
        <div className="flex items-start gap-1">
          {mine ? <Button size="icon" variant="ghost" onClick={del} data-testid={`post-delete-${post.id}`} aria-label="Delete"><Trash2 size={16} /></Button>
            : <Button size="icon" variant="ghost" onClick={() => report(api, "post", post.id)} data-testid={`post-report-${post.id}`} aria-label="Report"><Flag size={16} /></Button>}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {post.kind === "question" && <Pill cls="bg-sky-50 text-sky-800 border-sky-200"><HelpCircle size={11} />Question{post.accepted_comment_id ? " · solved" : ""}</Pill>}
        {L && <Pill cls="bg-amber-50 text-amber-800 border-amber-200"><L.icon size={11} />{L.label}</Pill>}
        {post.city && <Pill><MapPin size={11} />{post.city}</Pill>}
        {post.group_name && <Link to={`/community/groups/${post.group_id}`}><Pill cls="bg-slate-900 text-white border-slate-900">{post.group_name}</Pill></Link>}
      </div>
      {post.title && <h3 className="mt-3 font-bold text-lg text-slate-900" data-testid={`post-title-${post.id}`}>{post.title}</h3>}
      <p className="mt-2 text-slate-700 whitespace-pre-line leading-relaxed">{post.text}</p>
      {img && <img src={img} alt="" className="mt-4 rounded-md max-h-[420px] w-full object-cover" data-testid={`post-image-${post.id}`} />}
      <div className="mt-4 flex gap-1 border-t border-slate-100 pt-3">
        <Button variant="ghost" size="sm" onClick={like} data-testid={`post-like-${post.id}`} className={post.liked ? "text-rose-600" : ""}>
          <Heart size={16} fill={post.liked ? "currentColor" : "none"} />{post.likes_count}
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setOpen(!open)} data-testid={`post-comments-toggle-${post.id}`}>
          <MessageCircle size={16} />{post.comments_count} {post.kind === "question" ? (post.comments_count === 1 ? "answer" : "answers") : (post.comments_count === 1 ? "comment" : "comments")}
        </Button>
        {post.kind === "listing" && post.author_id !== me.user_id && (
          <Button asChild variant="ghost" size="sm" data-testid={`post-message-${post.id}`}><Link to={`/messages/${post.author_id}`}>Message</Link></Button>
        )}
      </div>
      {open && <Comments post={post} onAccepted={(id) => setPost({ ...post, accepted_comment_id: id })} onCount={(d) => setPost((p) => ({ ...p, comments_count: p.comments_count + d }))} />}
    </article>
  );
}
